import { Fragment, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { StyleSheet, View } from "react-native";

import { useContentResource } from "@ctv/core";

import { UploadDocumentResponse } from "../../../apis";
import { RegistrationDocument, RegistrationFormValue } from "../../../types";
import { FieldRow } from "../../FieldRow";

import {
  Checkbox,
  Document,
  DocumentField,
  formatMessage,
  Text,
  Token,
} from "@ctv/shared-core/src";

type Props = {
  document: Document;
  index: number;
  documentArrayMaxItem?: number;
  documentValue?: RegistrationDocument[];
  handleUpload(
    documentType: string,
    file: File,
    index: number
  ): Promise<UploadDocumentResponse>;
};

export default function CompanyDocumentItem({
  document,
  documentValue,
  index,
  documentArrayMaxItem,
  handleUpload,
}: Props) {
  const cr = useContentResource().CorporateRegistration;
  const [isChecked, setIsChecked] = useState(
    getCheckBoxDefaultValue(document, documentValue)
  );

  const { control } = useFormContext<RegistrationFormValue>();
  const {
    field: { onChange },
  } = useController({
    control,
    name: `document.${document.docType}`,
  });

  return (
    <Fragment>
      <View style={index !== 0 && styles.documentRow}>
        <FieldRow
          style={styles.fieldRow}
          label={document.docType === null ? undefined : document.label}
          subLabel={document.subLabel}
          tooltip={document.tooltip}
          required={document.mandatory}
        >
          <View>
            <DocumentField
              name={`document.${document.docType}`}
              document={document}
              helper={formatMessage(cr.documentInfo, {
                size: document.maxSizeInMB,
                extension: document.extensions.join("/"),
              })}
              onChange={handleUpload}
              disabled={isChecked}
              maxItem={documentArrayMaxItem}
              documentExtensionCr={cr.documentExtension}
              documentFileSizeCr={cr.documentFileSize}
              documentRequiredCr={cr.documentRequired}
            />
            {!!document.checkbox && (
              <View style={document.docType ? styles.documentRow : undefined}>
                <Checkbox
                  checked={isChecked}
                  onChange={(val) => {
                    if (val) {
                      onChange(`document.${document.docType}`, undefined);
                    }

                    setIsChecked(val);
                  }}
                >
                  <Text variant="ui-tiny" ink="black-secondary">
                    {document.checkbox.label}
                  </Text>
                </Checkbox>
              </View>
            )}
          </View>
        </FieldRow>
      </View>
      {isChecked &&
        document.checkbox?.documents.map((documentChild, index) => (
          <CompanyDocumentItem
            index={1}
            document={documentChild}
            handleUpload={handleUpload}
            key={`company_document_child__${index}__${documentChild.label}`}
          />
        ))}
    </Fragment>
  );
}

const styles = StyleSheet.create({
  content: {
    width: 640,
  },
  back: {
    alignSelf: "flex-start",
  },
  section: {
    marginTop: Token.spacing.m,
    marginBottom: Token.spacing.ml,
  },
  card: {
    padding: Token.spacing.l,
  },
  fieldRow: {
    alignItems: "flex-start",
  },
  documentRow: {
    marginTop: Token.spacing.l,
  },
});

function getCheckBoxDefaultValue(
  document: Document,
  documentValue?: RegistrationDocument[]
) {
  return Boolean(
    documentValue &&
      document.checkbox?.documents.some((doc) =>
        documentValue.some(
          (val) => val.documentType === doc.docType && val.fileNameEncrypted
        )
      )
  );
}
